import { useMutation } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { TILE_CONTENT } from '@mixtiles/web-backend-shared'
import { CLOUD_SERVICES } from 'components/CloudTileUploader/CloudTileUploader.consts'
import { UPLOAD_SRC } from 'components/PhotoBank/PhotoBank.consts'
import { SNACKBAR_VARIANTS } from 'elements/Snackbar/Snackbar.consts'
import { analytics, EVENT_NAMES } from 'services/Analytics/Analytics'
import { logger } from 'services/logger'
import { usePhotoBankStore, useTilesStore } from 'stores/RootStoreProvider'
import { CART_UPSELL_TEMPLATE_CATEGORY } from '../../cartUpsells.consts'
import type { CartUpsellAnalyticsProps } from '../../cartUpsells.types'
import { markUpsellApplied } from '../../cartUpsellsStorage'
import type { AiWallOffer } from '../aiWall.schemas'
import type { AiWallUpsell, AiWallUpsellId } from '../aiWall.types'

export function useAddAiWallToBag({
  upsell,
  analyticsProps,
  onAdded,
}: {
  upsell: AiWallUpsell<AiWallUpsellId>
  analyticsProps: CartUpsellAnalyticsProps
  onAdded: () => void
}) {
  const photoBankStore = usePhotoBankStore()
  const tilesStore = useTilesStore()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const uploadAiWallResult = async (
    offer: AiWallOffer,
    resultUrl: string
  ): Promise<string> => {
    const {
      photos: [uploadedPhoto],
    } = await photoBankStore.uploadPhotos({
      cloudServiceName: CLOUD_SERVICES.REMOTE,
      files: [{ url: resultUrl, width: offer.width, height: offer.height }],
      onUploadFailed: (uid) =>
        logger.error('Cart upsell result upload failed', {
          upsellId: upsell.id,
          uid,
          jobId: offer.jobId,
        }),
      uploadSrc: UPLOAD_SRC.AI_RESULT,
      withFaceDetection: false,
      filterSmallFaces: false,
    })
    return uploadedPhoto.uid
  }

  return useMutation({
    mutationFn: async ({
      offer,
      resultIndexes,
    }: {
      offer: AiWallOffer
      // The results the user kept on, in wall order.
      resultIndexes: number[]
    }) => {
      // Uploaded only on accept, so declined results stay out of the photo bank.
      const uploadedResults = await Promise.all(
        resultIndexes.map(async (resultIndex) => ({
          resultIndex,
          uid: await uploadAiWallResult(offer, offer.results[resultIndex].url),
        }))
      )
      markUpsellApplied(upsell.id)
      const addedTiles = tilesStore.addTiles(
        uploadedResults.map(({ resultIndex, uid }) => ({
          tileContent: TILE_CONTENT.AI_GEN_PHOTO,
          uid,
          ...upsell.tile,
          aiTemplate: {
            templateId: upsell.templateSlug,
            templateCategory: CART_UPSELL_TEMPLATE_CATEGORY,
            jobId: offer.jobId,
            resultIndex,
            generationVariantIndex: upsell.variantIndexes[resultIndex],
          },
          sources: offer.sourcePhotoUids.map((sourcePhotoUid) => ({
            uid: sourcePhotoUid,
          })),
          metadata: { cartUpsellId: upsell.id },
        }))
      )
      analytics.track(EVENT_NAMES.CART_UPSELL_ADDED_TO_BAG, {
        ...analyticsProps,
        'Tiles Added': addedTiles.length,
      })
    },
    onSuccess: () => onAdded(),
    onError: (error) => {
      logger.error('Failed to add a cart upsell to the bag', {
        upsellId: upsell.id,
        error,
      })
      enqueueSnackbar('', {
        variant: SNACKBAR_VARIANTS.COMMON,
        name: 'Cart Upsell Add To Bag Failed',
        title: t('general.errors.general_title_short'),
        subtitle: t('general.errors.general_description_short'),
      })
    },
  })
}
